import { createColumnHelper } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils/cn';

import { FilterType, OpenLot } from '../_openapi/portfolio-optimizer';
import { getAvailableAssetClasses, getAvailableAssetTypes, getAvailableSubAssetTypes } from '../utils/assetUtils';
import { AssetClassTag } from '../widgets/AssetClassTag';
import { baseCol, dateCol, moneyCol, numberCol, textCol } from '../widgets/Table/columnHelpers/colDef';
import { FlagCell } from '../widgets/Table/FlagCell';

const columnHelper = createColumnHelper<OpenLot>();
export const openLotListColumns = [
    columnHelper.accessor('pId', textCol({ id: 'pId' })),
    columnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    columnHelper.accessor('portfolioName', textCol({ id: 'portfolioName' })),
    columnHelper.accessor('householdName', textCol({ id: 'householdName' })),
    columnHelper.accessor('aC', textCol({ id: 'aC' })),
    columnHelper.accessor('sId', textCol({ id: 'sId' })),
    columnHelper.accessor('openLotId', textCol({ id: 'openLotId' })),
    columnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    columnHelper.accessor('description', textCol({ id: 'description' })),
    columnHelper.accessor('assetClass', {
        ...baseCol({ id: 'assetClass' }),
        cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
        meta: {
            filterOptionsFn: getAvailableAssetClasses,
            filterType: FilterType.LIST,
        },
    }),
    columnHelper.accessor(
        'assetType',
        textCol({
            id: 'assetType',
            meta: {
                filterOptionsFn: getAvailableAssetTypes,
                filterType: FilterType.LIST,
            },
        }),
    ),
    columnHelper.accessor(
        'subAssetType',
        textCol({
            id: 'subAssetType',
            meta: {
                filterOptionsFn: getAvailableSubAssetTypes,
                filterType: FilterType.LIST,
            },
        }),
    ),
    columnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    columnHelper.accessor('closePrice', moneyCol({ id: 'closePrice' })),
    columnHelper.accessor('marketValue', moneyCol({ id: 'marketValue' })),
    columnHelper.accessor('netExposure', moneyCol({ id: 'netExposure' })),
    columnHelper.accessor('grossExposure', moneyCol({ id: 'grossExposure' })),
    columnHelper.accessor('side', textCol({ id: 'side' })),
    columnHelper.accessor('costBasis', moneyCol({ id: 'costBasis' })),
    columnHelper.accessor('pnlUnrealized', moneyCol({ id: 'pnlUnrealized' })),
    columnHelper.accessor('returnUnrealized', numberCol({ id: 'returnUnrealized', format: { style: 'percent' } })),
    columnHelper.accessor('openDate', dateCol({ id: 'openDate' })),
    columnHelper.display({
        id: 'term',
        header: 'Term',
        cell: ({ row }) => {
            const openDate = row.original.openDate;

            if (!openDate) {
                return null;
            }

            const today = new Date();
            const open = new Date(openDate);
            const daysDiff = (today.getTime() - open.getTime()) / (1000 * 60 * 60 * 24);
            const isLongTerm = daysDiff > 365;

            const label = isLongTerm ? 'Long-term' : 'Short-term';
            const colorClasses = isLongTerm
                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200'
                : 'bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200';

            return <Badge className={cn('border-transparent', colorClasses)}>{label}</Badge>;
        },
    }),
    columnHelper.accessor('referencePortfolio', textCol({ id: 'referencePortfolio' })),
    columnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    columnHelper.accessor('investmentAlgo', textCol({ id: 'investmentAlgo' })),
    columnHelper.accessor('investmentSleeve', textCol({ id: 'investmentSleeve' })),
    columnHelper.accessor('nav', moneyCol({ id: 'nav' })),
    columnHelper.accessor(
        'marketValuePercentOfNav',
        numberCol({ id: 'marketValuePercentOfNav', format: { style: 'percent' } }),
    ),
    columnHelper.accessor('scId', textCol({ id: 'scId' })),
    columnHelper.accessor('coId', textCol({ id: 'coId' })),
    columnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
    columnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    columnHelper.accessor('primaryExchange', textCol({ id: 'primaryExchange' })),
    columnHelper.accessor('cusip', textCol({ id: 'cusip' })),
    columnHelper.accessor('isin', textCol({ id: 'isin' })),
    columnHelper.accessor('sedol', textCol({ id: 'sedol' })),
    columnHelper.accessor('country', {
        ...baseCol({ id: 'country' }),
        cell: ({ getValue }) => <FlagCell alpha3={getValue()} />,
    }),
    columnHelper.accessor('mic', textCol({ id: 'mic' })),
    columnHelper.accessor('gicsCode', textCol({ id: 'gicsCode' })),
    columnHelper.accessor('gicsSector', textCol({ id: 'gicsSector' })),
    columnHelper.accessor('gicsIndustry', textCol({ id: 'gicsIndustry' })),
    columnHelper.accessor('issuer', textCol({ id: 'issuer' })),
    columnHelper.accessor('coupon', numberCol({ id: 'coupon' })),
    columnHelper.accessor('maturityDate', dateCol({ id: 'maturityDate' })),
    columnHelper.accessor('issueDate', dateCol({ id: 'issueDate' })),
    columnHelper.accessor('expirationDate', dateCol({ id: 'expirationDate' })),
    columnHelper.accessor('firstNoticeDate', dateCol({ id: 'firstNoticeDate' })),
    columnHelper.accessor('settlementMethod', textCol({ id: 'settlementMethod' })),
    columnHelper.accessor('minTick', numberCol({ id: 'minTick' })),
    columnHelper.accessor('putCall', textCol({ id: 'putCall' })),
    columnHelper.accessor('underlyingSymbol', textCol({ id: 'underlyingSymbol' })),
    columnHelper.accessor('brokerId', textCol({ id: 'brokerId' })),
    columnHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
];
